<script setup>
import {ref, onMounted, watch, computed} from "vue";
import Seal  from "@/components/dashboard/components/Seal.vue";
import {fetchWidgetData} from "@/services/api/widgetData.js";
import {useI18n} from "vue-i18n";
import { formatCategoriesDashboard } from '@/utils/dateTimeFormat.js';
import {handleLoginError} from "@/utils/errors.js";
import ptBR from 'date-fns/locale/pt-BR';
import es from 'date-fns/locale/es';
import enUS from 'date-fns/locale/en-US';

const localesMap = {
    'en': enUS,
    'pt': ptBR,
    'es': es,
};
const { locale, t } = useI18n();
const responseData = ref(null);
const series = ref([]);
const props = defineProps({
    jsonData: Object,
    interval: Object,
});

const urlData = computed(() => {
    const url_value = props.jsonData.url_value;
    return `${url_value}`;
});
const showLabelX = computed(() => props.jsonData.selected);
const maxValue = ref(null);
const minValue = ref(null);
const roundedMax= ref(null);
const roundedMin = ref(null);

const categories = computed(() => {
    const currentLocale = localesMap[locale.value];
    const startDate = props.interval.start ?? props.interval.series.start;
    const endDate = props.interval.end ?? props.interval.series.end;

    return formatCategoriesDashboard(
        responseData.value.data,
        startDate,
        endDate,
        currentLocale,
        locale.value
    );
});

const title = computed(() => {
    if (props.jsonData && props.jsonData.i18n && props.jsonData.i18n[locale.value]) {
        return props.jsonData.i18n[locale.value].title;
    }
});

const color = computed(() => {
    if (props.jsonData && props.jsonData.style) {
        return props.jsonData.style.color;
    }
});


onMounted(async () => {
    try {
        await getData();
    } catch (error) {
        if(error.status === 401 || error.status === 403 ) {
            message.error(t("common.messages.errors.403"));
        } else {
            message.error(t("common.messages.errors.generic"));
        }
    }
});

async function getData() {
    try {
        const response = await fetchWidgetData(urlData.value, props.interval);
        responseData.value = response;
        series.value = [
            {
                name: title.value,
                data: response.data.map(item => item.value)
            }
        ];
        const dataArray = series.value[0].data;
        maxValue.value = Math.max(...dataArray);
        minValue.value = Math.min(...dataArray);

        roundedMin.value = getRoundedMin(minValue.value, maxValue.value);
        roundedMax.value = getRoundedMax(minValue.value, maxValue.value);

    } catch (error) {
        responseData.value = null;
        return Promise.reject(error);
    }
}

function getRoundedMin(minValue, maxValue) {
    const range = maxValue - minValue;
    let result = Math.floor(minValue - range) + 5;

    if (result <= 0) {
        return minValue;
    }
    return result;
}

function getRoundedMax(minValue, maxValue) {
    const range = maxValue - minValue;
    let result = Math.ceil(maxValue + range) - 5;

    if (result <= 0) {
        return maxValue;
    }
    return result;
}

watch(
    [() => props.interval],
    async () => {
        await getData();
    },
);

const total = computed(() => {
    return {
        chart: {
            type: "area",
            zoom: {
                enabled: true,
            },
            style: {
                top: "10px",
            },
        },
        colors: [color.value],
        plotOptions: {
            bar: {
                columnWidth: "55%",
            },
        },
        dataLabels: {
            enabled: false,
        },

        grid: {
            show: false,
            borderColor: "#ECEEF2",
        },
        responsive: [
            {
                breakpoint: 1238,
                options: {
                    xaxis: {
                        tickAmount: 3,
                        labels: {
                            show: true,
                            rotate: -45,
                            style: {
                                fontSize: '10px'
                            },
                        }
                    },
                    yaxis: {
                        labels: {
                            show: true,
                            style: {
                                fontSize: '10px'
                            }
                        }
                    }
                }
            }
        ],
        xaxis: {
            categories: categories.value,
            axisTicks: {
                show: false,
                color: "#ECEEF2",
            },
            axisBorder: {
                show: false,
                color: "#ECEEF2",
            },
            labels: {
                show: showLabelX.value,
                style: {
                    colors: "#8695AA",
                    fontSize: "12px",
                },
            },
        },
        yaxis: {
            show: true,
            tickAmount: 3,
            max: roundedMax.value,
            min: roundedMin.value,
            labels: {
                formatter: val => new Intl.NumberFormat(locale.value, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(val),
                style: {
                    colors: "#64748B",
                    fontSize: "12px",
                },
            },
            axisBorder: {
                show: false,
                color: "#ECEEF2",
            },
            axisTicks: {
                show: false,
                color: "#ECEEF2",
            },
        },
        legend: {
            show: true,
            position: "bottom",
            fontSize: "12px",
            horizontalAlign: "left",
            itemMargin: {
                horizontal: 8,
                vertical: 0,
            },
            labels: {
                colors: "#64748B",
            },
            markers: {
                size: 7,
                offsetX: -2,
                offsetY: -0.5,
                shape: "diamond",
            },
        },
    }
});

</script>
<template>
    <v-card v-if="responseData" class="total trezo-card border-radius d-block p-0 mt-5 border-0 shadow-none">
        <seal v-if="responseData" :data="responseData.accumulatedValue" />
        <div v-if="maxValue" class="chart ml-auto mr-auto" >
            <apexchart
                type="area"
                :options="total"
                :series="series"
                :height="'200px'"
            ></apexchart>
        </div>
        <div v-else class="d-flex align-center justify-center" style="height: 200px;">
            <v-progress-circular
                indeterminate
                color="grey"
                size="64"
                width="4"
            ></v-progress-circular>
        </div>
    </v-card>
</template>

<style lang="scss" scoped>
.total {
    .chart {
        margin: {
            bottom: -15px;
            left: -10px;
            top: -5px;
        }
    }
}

</style>

